/*
 * Active-Field-Highlight
 *
 * Pure Helpers fuer die "aktiver Absatz"-Hervorhebung in Editor-Panes.
 * - Marker (>, <) im aktuellen Eingabe-Pane des aktiven Absatzes bleiben voll;
 *   alle anderen Marker werden auf EDITOR_MARKER_DIM zurueckgestuft.
 * - Pane↔Field-Mapping: Main↔text, Outline↔note, Context↔info.
 *
 * Trennt UI-Logik von Pane-Implementierungen, damit Tests leichtgewichtig
 * bleiben.
 */

import { Style } from '../config.js'

// Pane-Identitaet aus Sicht des Highlights. Kein Bezug zur layout.PaneId-Numerik:
// Werte werden ausschliesslich in dieser Datei zum Mapping benutzt.
export const MarkerPane = Object.freeze({
    MAIN: 'main',
    OUTLINE: 'outline',
    CONTEXT: 'context'
})

// Welches Cursor-Field gehoert zu welchem Pane?
const PANE_FIELD = Object.freeze({
    [MarkerPane.MAIN]: 'text',
    [MarkerPane.OUTLINE]: 'note',
    [MarkerPane.CONTEXT]: 'info'
})

/**
 * Liefert true, wenn der gegebene Absatz/Pane das aktive Cursor-Feld bedient.
 * @param {string} pane              MarkerPane.MAIN | OUTLINE | CONTEXT
 * @param {*}       paragraphId      Absatz-ID, an der der Marker steht
 * @param {{paragraphId:*, field:string}|null} cursor  EditorModel.cursor
 * @returns {boolean}
 */
export function isActiveMarker(pane, paragraphId, cursor) {
    if (!cursor) return false
    if (cursor.paragraphId !== paragraphId) return false
    const expectedField = PANE_FIELD[pane]
    if (!expectedField) return false
    const actualField = cursor.field || 'text'
    return actualField === expectedField
}

/**
 * Waehlt EDITOR_MARKER (voll) oder EDITOR_MARKER_DIM (gedimmt).
 * @param {string} pane
 * @param {*} paragraphId
 * @param {{paragraphId:*, field:string}|null} cursor
 * @returns {string} Style-Konstante
 */
export function pickMarkerStyle(pane, paragraphId, cursor) {
    return isActiveMarker(pane, paragraphId, cursor)
        ? Style.EDITOR_MARKER
        : Style.EDITOR_MARKER_DIM
}

/**
 * Marker-Style fuer Separator-Marker (>/<). Anders als bei normalen
 * Absaetzen ist die Pane-Aktivitaet auf Separatoren nicht ueber
 * cursor.field bestimmt — Separatoren haben nur das `text`-Field —
 * sondern ueber sepState.pane (welches Pane gerade das Title-Editing
 * uebernimmt). Ohne sepState (z.B. Cursor steht woanders) sind alle
 * Separator-Marker dim.
 *
 * @param {string} pane                          MarkerPane.MAIN|OUTLINE|CONTEXT
 * @param {*} paragraphId                        Separator-ID
 * @param {{paragraphId:*, field:string}|null} cursor
 * @param {{pane:string}|null} sepState          Separator-Edit-State (oder null)
 * @returns {string}
 */
export function pickSeparatorMarkerStyle(pane, paragraphId, cursor, sepState) {
    if (!cursor || cursor.paragraphId !== paragraphId) {
        return Style.EDITOR_MARKER_DIM
    }
    const editPane = sepState && sepState.pane ? sepState.pane : null
    return editPane === pane ? Style.EDITOR_MARKER : Style.EDITOR_MARKER_DIM
}
